/**
 * Saved dashboard views (WPS-232 #1).
 *
 * A view is a named snapshot of a user's dashboard: which cards are on it and
 * in what order, each card's width, the cards they built, and each card's own
 * filters and columns. It is stored as a generic saved view (the same
 * ``TableView`` the list pages use) under ``table_id = "dashboard:<role>"``, so
 * a view is only ever offered on the role dashboard it was built from. A view
 * can be shared with the owner's organisation; a shared view is read-only for
 * everyone but its owner, who alone can update, rename, unshare or delete it.
 *
 * This module holds the pure snapshot / restore logic; the name dialog is in
 * dashboard-view-dialog.tsx and the dashboard page owns the menu and layout.
 */

import type { ProductFilterValue } from '@/shared/components/ui/product-filter-menu'
import {
  readTableColumnPreferences,
  writeTableColumnPreferences,
  type TableColumnPreferences,
} from '@/admin/hooks/use-table-column-preferences'
import {
  readTableFilterPreferences,
  writeTableFilterPreferences,
} from '@/admin/hooks/use-table-filter-preferences'
import type { TileDef } from './dashboard-types'

/** The saved-view bucket for one role's dashboard. */
export const dashboardViewTableId = (role: string) => `dashboard:${role}`

export interface DashboardViewLayout {
  order: string[]
  removed: string[]
  spans: Record<string, number>
}

interface TilePrefsSnapshot {
  columns?: TableColumnPreferences
  filters?: ProductFilterValue
}

export interface DashboardViewConfig {
  version: 1
  layout: DashboardViewLayout
  /** User-built cards the layout uses, so a teammate applying it gets them too. */
  cards: Record<string, TileDef>
  /** Per-card filters / columns, keyed by the card table's preference id. */
  tilePrefs: Record<string, TilePrefsSnapshot>
}

// A card keeps separate preferences at rest and when expanded (see TileCard /
// ExpandedTile), so a view captures both.
const tileTableIds = (key: string) => [
  `dashboard-tile-${key}`,
  `dashboard-tile-${key}-expanded`,
]

const isEmpty = (v: object | undefined) => !v || Object.keys(v).length === 0

/** Capture the current dashboard as a view config. */
export function snapshotDashboardView({
  layout,
  userCards,
  tenantSlug,
}: {
  layout: DashboardViewLayout
  userCards: Record<string, TileDef>
  tenantSlug: string | null
}): DashboardViewConfig {
  const cards: Record<string, TileDef> = {}
  const tilePrefs: Record<string, TilePrefsSnapshot> = {}
  for (const key of layout.order) {
    if (userCards[key]) cards[key] = userCards[key]
    for (const id of tileTableIds(key)) {
      const columns = readTableColumnPreferences(id, tenantSlug)
      const filters = readTableFilterPreferences(id, tenantSlug)
      const prefs: TilePrefsSnapshot = {}
      if (!isEmpty(columns.columnVisibility) || (columns.columnOrder?.length ?? 0) > 0) {
        prefs.columns = columns
      }
      if (!isEmpty(filters)) prefs.filters = filters
      if (!isEmpty(prefs)) tilePrefs[id] = prefs
    }
  }
  return {
    version: 1,
    layout: {
      order: [...layout.order],
      removed: [...layout.removed],
      spans: { ...layout.spans },
    },
    cards,
    tilePrefs,
  }
}

/**
 * Restore a view's per-card filters and columns. Every card in the view is
 * written - a card with nothing saved is reset - so the dashboard ends up
 * exactly as the view was saved, not merged with whatever was there before.
 * Cards read these on mount, so the caller remounts the grid afterwards.
 */
export function applyDashboardViewPrefs(
  config: DashboardViewConfig,
  tenantSlug: string | null,
) {
  for (const key of config.layout.order) {
    for (const id of tileTableIds(key)) {
      const prefs = config.tilePrefs[id] ?? {}
      writeTableColumnPreferences(id, tenantSlug, prefs.columns ?? {})
      writeTableFilterPreferences(id, tenantSlug, prefs.filters ?? {})
    }
  }
}

/**
 * A view as it can actually be shown now: cards that no longer exist (deleted
 * since the view was saved) are dropped from its layout. Applying a view and
 * checking it for unsaved changes both use this, so a view that mentions a
 * deleted card never looks permanently "changed".
 */
export function restrictToKnownCards(
  config: DashboardViewConfig,
  isKnown: (key: string) => boolean,
): DashboardViewConfig {
  const known = (k: string) => isKnown(k) || !!config.cards[k]
  return {
    ...config,
    layout: {
      ...config.layout,
      order: config.layout.order.filter(known),
      removed: config.layout.removed.filter(known),
    },
  }
}

/** Parse a stored view config defensively; null when it is not a dashboard view. */
export function parseDashboardViewConfig(raw: unknown): DashboardViewConfig | null {
  if (!raw || typeof raw !== 'object') return null
  const c = raw as Partial<DashboardViewConfig>
  const layout = c.layout
  if (!layout || !Array.isArray(layout.order)) return null
  return {
    version: 1,
    layout: {
      order: layout.order.filter((k): k is string => typeof k === 'string'),
      removed: Array.isArray(layout.removed)
        ? layout.removed.filter((k): k is string => typeof k === 'string')
        : [],
      spans: layout.spans && typeof layout.spans === 'object' ? layout.spans : {},
    },
    cards: c.cards && typeof c.cards === 'object' ? c.cards : {},
    tilePrefs: c.tilePrefs && typeof c.tilePrefs === 'object' ? c.tilePrefs : {},
  }
}

// JSON with object keys sorted, so two configs compare equal regardless of key
// order. The server stores a view as JSONB, which does not keep key order, so a
// plain JSON.stringify would call a just-saved view "changed".
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(',')}}`
  }
  return JSON.stringify(value)
}

/** Whether two view configs describe the same dashboard. */
export function sameDashboardView(a: DashboardViewConfig, b: DashboardViewConfig) {
  return canonical(a) === canonical(b)
}
